import { useSearchParams } from 'react-router-dom'
import { Box, Stack, Typography } from '@mui/material'
import { Tabs, TABS_CONTENT_GAP } from '@repo/ui'

import { Header } from 'components'
import { AFFILIATES_TABS, type AffiliatesTab } from 'routes'

// Imported from where VRG-665 and VRG-658 built them, not copied (§71): one set of components, so
// this page and anything else that shows affiliates or referrals can never drift apart. Moving the
// files would turn every open referrals branch's next fold into a rename conflict for no behaviour.
import { ALL_REFERRALS_ANCHOR } from '../InternalAnalyticsPage/components/affiliates/AffiliatesCards'
import { AffiliatesPanel } from '../InternalAnalyticsPage/components/affiliates/AffiliatesPanel'
import { ReferralsTab } from '../SignupsPage/referrals/ReferralsTab'

const TAB_LABELS: Record<AffiliatesTab, string> = {
  refer: 'Refer & earn',
  insiders: 'Insiders',
}
const TABS = AFFILIATES_TABS.map((value) => ({ value, label: TAB_LABELS[value] }))

/**
 * `/internal/gtm/affiliates` — Traction channel #14 (affiliate programs). VRG-684 (Spencer,
 * 2026-10-08) renamed VRG-678's "Insiders" page to Affiliates, because it covers BOTH programmes,
 * and gave each its own tab (`?tab=`):
 *
 *   Refer & earn  brands referring brands (Refer a brand): the VRG-665 dashboard, then every
 *                 referral with its status, credits and staff actions — the table that was
 *                 People → Referrals (VRG-658), moved here whole
 *   Insiders      Vera Insiders: vera-2's Insider prospects and the FirstPromoter figures
 *
 * Old addresses redirect with their params: `/internal/gtm/insiders`, `/internal/analytics?tab=
 * affiliates` and `/internal/people?tab=referrals` (the last onto Refer & earn). People keeps its
 * "Referred by" column: who referred a person is a fact about the person.
 *
 * Gate: `internal_gtm_view`; the API adds a platform admin or manager role on every read.
 */
export const GtmAffiliatesPage = () => {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const tab: AffiliatesTab = (AFFILIATES_TABS as readonly string[]).includes(requested ?? '')
    ? (requested as AffiliatesTab)
    : 'refer'
  const setTab = (value: string) => {
    const next = new URLSearchParams(params)
    next.set('tab', value)
    setParams(next, { replace: true })
  }

  return (
    <>
      <Header
        title="Affiliates"
        description="The two referral programmes: brands that refer brands and earn activations, and Vera Insiders, who introduce brands for a cash commission."
      />
      <Tabs redirect={false} tabs={TABS} value={tab} onChange={setTab} />
      <Box sx={{ mt: `${TABS_CONTENT_GAP}px` }}>
        {tab === 'insiders' ? (
          <AffiliatesPanel section="insiders" />
        ) : (
          <Stack spacing={8} useFlexGap>
            <AffiliatesPanel section="refer" />
            <Stack spacing={4} id={ALL_REFERRALS_ANCHOR} sx={{ scrollMarginTop: 120 }}>
              <Typography variant="h6">All referrals</Typography>
              <ReferralsTab />
            </Stack>
          </Stack>
        )}
      </Box>
    </>
  )
}
